import { useState, useEffect, useCallback } from 'react';
import { adminAuthAPI, adminConfigAPI, adminSmtpAPI, adminSeoAPI } from '../../lib/adminApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Loader2, Save, Lock, Shield, Mail, Send, Globe, Search,
  FileText, Code, Eye, EyeOff, Plus, Trash2, CheckCircle, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

export default function AdminSettingsPage() {
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [saving, setSaving] = useState(false);

  // SMTP State
  const [smtpForm, setSmtpForm] = useState({
    SMTP_HOST: '', SMTP_PORT: '587', SMTP_EMAIL: '', SMTP_PASSWORD: '',
    SMTP_FROM_NAME: 'DotCall', SMTP_ENABLED: 'false'
  });
  const [smtpLoading, setSmtpLoading] = useState(true);
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);

  // SEO State
  const [seoForm, setSeoForm] = useState({
    site_title: '', meta_description: '', meta_keywords: '',
    og_title: '', og_description: '', og_image: '', og_type: 'website',
    twitter_card: 'summary_large_image', canonical_url: '', robots: 'index, follow',
    structured_data: {}, custom_head_tags: '',
    sitemap_enabled: true, sitemap_pages: []
  });
  const [seoLoading, setSeoLoading] = useState(true);
  const [seoSaving, setSeoSaving] = useState(false);
  const [structuredDataStr, setStructuredDataStr] = useState('');

  // Load SMTP Settings
  const loadSmtp = useCallback(async () => {
    setSmtpLoading(true);
    try {
      const keys = ['SMTP_HOST', 'SMTP_PORT', 'SMTP_EMAIL', 'SMTP_PASSWORD', 'SMTP_FROM_NAME', 'SMTP_ENABLED'];
      const results = await Promise.all(keys.map(k => adminConfigAPI.get(k).catch(() => ({ data: { value: '' } }))));
      const form = {};
      keys.forEach((k, i) => { form[k] = results[i]?.data?.value || smtpForm[k] || ''; });
      setSmtpForm(form);
    } catch (err) { console.error(err); }
    finally { setSmtpLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load SEO Settings
  const loadSeo = useCallback(async () => {
    setSeoLoading(true);
    try {
      const res = await adminSeoAPI.get();
      setSeoForm(res.data);
      setStructuredDataStr(JSON.stringify(res.data.structured_data || {}, null, 2));
    } catch (err) { console.error(err); }
    finally { setSeoLoading(false); }
  }, []);

  useEffect(() => { loadSmtp(); loadSeo(); }, [loadSmtp, loadSeo]);

  const handleChangePassword = async () => {
    if (!passwordForm.current_password || !passwordForm.new_password) {
      toast.error('All fields are required'); return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match'); return;
    }
    if (passwordForm.new_password.length < 8) {
      toast.error('Password must be at least 8 characters'); return;
    }
    setSaving(true);
    try {
      await adminAuthAPI.changePassword({ current_password: passwordForm.current_password, new_password: passwordForm.new_password });
      toast.success('Password changed successfully');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to change password'); }
    finally { setSaving(false); }
  };

  const handleSaveSmtp = async () => {
    setSmtpSaving(true);
    try {
      for (const [key, value] of Object.entries(smtpForm)) {
        await adminConfigAPI.set(key, value);
      }
      toast.success('SMTP settings saved');
    } catch (err) { toast.error('Failed to save SMTP settings'); }
    finally { setSmtpSaving(false); }
  };

  const handleTestSmtp = async () => {
    setSmtpTesting(true);
    try {
      const res = await adminSmtpAPI.sendTest();
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test email failed');
    } finally { setSmtpTesting(false); }
  };

  const handleSaveSeo = async () => {
    setSeoSaving(true);
    try {
      let sd = {};
      try { sd = JSON.parse(structuredDataStr); } catch { sd = seoForm.structured_data; }
      const data = { ...seoForm, structured_data: sd };
      await adminSeoAPI.update(data);
      toast.success('SEO settings saved');
    } catch (err) { toast.error('Failed to save SEO settings'); }
    finally { setSeoSaving(false); }
  };

  const addSitemapPage = () => {
    setSeoForm(prev => ({
      ...prev,
      sitemap_pages: [...(prev.sitemap_pages || []), { url: '/', priority: '0.5', changefreq: 'monthly' }]
    }));
  };

  const removeSitemapPage = (index) => {
    setSeoForm(prev => ({
      ...prev,
      sitemap_pages: prev.sitemap_pages.filter((_, i) => i !== index)
    }));
  };

  const updateSitemapPage = (index, field, value) => {
    setSeoForm(prev => ({
      ...prev,
      sitemap_pages: prev.sitemap_pages.map((p, i) => i === index ? { ...p, [field]: value } : p)
    }));
  };

  return (
    <div className="space-y-8 max-w-4xl" data-testid="admin-settings-page">
      <div>
        <h1 className="text-3xl font-bold font-['Outfit'] text-white">Admin Settings</h1>
        <p className="text-slate-400 mt-1">Manage account, email, and SEO settings</p>
      </div>

      <Tabs defaultValue="password" className="space-y-6">
        <TabsList className="bg-slate-900/50 border border-white/5 p-1 flex-wrap">
          <TabsTrigger value="password" className="data-[state=active]:bg-red-600/20 data-[state=active]:text-red-300 text-slate-400">
            <Lock className="w-4 h-4 mr-2" /> Security
          </TabsTrigger>
          <TabsTrigger value="smtp" className="data-[state=active]:bg-amber-600/20 data-[state=active]:text-amber-300 text-slate-400"
            onClick={() => { if (smtpLoading) loadSmtp(); }}>
            <Mail className="w-4 h-4 mr-2" /> SMTP / Email
          </TabsTrigger>
          <TabsTrigger value="seo" className="data-[state=active]:bg-emerald-600/20 data-[state=active]:text-emerald-300 text-slate-400"
            onClick={() => { if (seoLoading) loadSeo(); }}>
            <Globe className="w-4 h-4 mr-2" /> SEO
          </TabsTrigger>
        </TabsList>

        {/* ===== SECURITY TAB ===== */}
        <TabsContent value="password">
          <div className="glass-card rounded-xl p-6" data-testid="change-password-section">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-red-600/20 flex items-center justify-center">
                <Lock className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold font-['Outfit'] text-white">Change Password</h3>
                <p className="text-sm text-slate-400">Update your admin password</p>
              </div>
            </div>
            <div className="space-y-4 max-w-md">
              <div>
                <Label className="text-slate-300 text-sm mb-2 block">Current Password</Label>
                <Input type="password" value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm(p => ({ ...p, current_password: e.target.value }))}
                  placeholder="Enter current password" data-testid="current-password-input"
                  className="bg-slate-950 border-white/10 text-white placeholder:text-slate-600" />
              </div>
              <div>
                <Label className="text-slate-300 text-sm mb-2 block">New Password</Label>
                <Input type="password" value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm(p => ({ ...p, new_password: e.target.value }))}
                  placeholder="Min 8 characters" data-testid="new-password-input"
                  className="bg-slate-950 border-white/10 text-white placeholder:text-slate-600" />
              </div>
              <div>
                <Label className="text-slate-300 text-sm mb-2 block">Confirm New Password</Label>
                <Input type="password" value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm(p => ({ ...p, confirm_password: e.target.value }))}
                  placeholder="Confirm new password" data-testid="confirm-password-input"
                  className="bg-slate-950 border-white/10 text-white placeholder:text-slate-600" />
              </div>
              <Button onClick={handleChangePassword} disabled={saving} data-testid="change-password-button"
                className="bg-red-600 hover:bg-red-700 text-white">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Change Password
              </Button>
            </div>
          </div>

          <div className="glass-card rounded-xl p-6 mt-6" data-testid="admin-info-section">
            <div className="flex items-center gap-3 mb-4">
              <Shield className="w-5 h-5 text-amber-400" />
              <h3 className="text-lg font-semibold font-['Outfit'] text-white">Admin Information</h3>
            </div>
            <div className="space-y-3 text-sm max-w-md">
              <div className="flex justify-between py-2 border-b border-white/5">
                <span className="text-slate-400">Default Email</span>
                <span className="text-white font-mono">admin@dotcall.ai</span>
              </div>
              <div className="flex justify-between py-2 border-b border-white/5">
                <span className="text-slate-400">Role</span>
                <span className="text-red-400 font-medium">Super Admin</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-slate-400">Access</span>
                <span className="text-white">Full Platform Control</span>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* ===== SMTP TAB ===== */}
        <TabsContent value="smtp">
          <div className="glass-card rounded-xl p-6" data-testid="smtp-section">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-600/20 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold font-['Outfit'] text-white">SMTP Configuration</h3>
                  <p className="text-sm text-slate-400">Connect Zoho Mail or any SMTP provider for automated emails</p>
                </div>
              </div>
              <Badge className={smtpForm.SMTP_ENABLED === 'true' ? 'bg-emerald-600/20 text-emerald-400 border-emerald-500/30' : 'bg-slate-800 text-slate-500 border-slate-600/30'}>
                {smtpForm.SMTP_ENABLED === 'true' ? <><CheckCircle className="w-3 h-3 mr-1" />Enabled</> : <><AlertCircle className="w-3 h-3 mr-1" />Disabled</>}
              </Badge>
            </div>

            {smtpLoading ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 text-amber-400 animate-spin" /></div>
            ) : (
              <div className="space-y-5">
                <div className="flex items-center gap-4 p-3 rounded-lg bg-white/[0.02] border border-white/5">
                  <Label className="text-slate-300 text-sm whitespace-nowrap">Enable SMTP</Label>
                  <button onClick={() => setSmtpForm(p => ({ ...p, SMTP_ENABLED: p.SMTP_ENABLED === 'true' ? 'false' : 'true' }))}
                    data-testid="smtp-toggle"
                    className={`relative w-11 h-6 rounded-full transition-colors ${smtpForm.SMTP_ENABLED === 'true' ? 'bg-emerald-600' : 'bg-slate-700'}`}>
                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${smtpForm.SMTP_ENABLED === 'true' ? 'translate-x-5' : ''}`} />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300 text-sm mb-2 block">SMTP Host</Label>
                    <Input value={smtpForm.SMTP_HOST} onChange={(e) => setSmtpForm(p => ({ ...p, SMTP_HOST: e.target.value }))}
                      placeholder="smtp.zoho.in or smtp.zoho.com" data-testid="smtp-host"
                      className="bg-slate-950 border-white/10 text-white" />
                  </div>
                  <div>
                    <Label className="text-slate-300 text-sm mb-2 block">SMTP Port</Label>
                    <Input value={smtpForm.SMTP_PORT} onChange={(e) => setSmtpForm(p => ({ ...p, SMTP_PORT: e.target.value }))}
                      placeholder="587" data-testid="smtp-port"
                      className="bg-slate-950 border-white/10 text-white" />
                  </div>
                  <div>
                    <Label className="text-slate-300 text-sm mb-2 block">Email Address</Label>
                    <Input value={smtpForm.SMTP_EMAIL} onChange={(e) => setSmtpForm(p => ({ ...p, SMTP_EMAIL: e.target.value }))}
                      placeholder="notifications@yourdomain.com" data-testid="smtp-email"
                      className="bg-slate-950 border-white/10 text-white" />
                  </div>
                  <div>
                    <Label className="text-slate-300 text-sm mb-2 block">App Password</Label>
                    <div className="relative">
                      <Input type={showSmtpPassword ? 'text' : 'password'} value={smtpForm.SMTP_PASSWORD}
                        onChange={(e) => setSmtpForm(p => ({ ...p, SMTP_PASSWORD: e.target.value }))}
                        placeholder="Zoho App Password" data-testid="smtp-password"
                        className="bg-slate-950 border-white/10 text-white pr-10" />
                      <button onClick={() => setShowSmtpPassword(!showSmtpPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white">
                        {showSmtpPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-slate-300 text-sm mb-2 block">From Name</Label>
                    <Input value={smtpForm.SMTP_FROM_NAME} onChange={(e) => setSmtpForm(p => ({ ...p, SMTP_FROM_NAME: e.target.value }))}
                      placeholder="DotCall" data-testid="smtp-from-name"
                      className="bg-slate-950 border-white/10 text-white" />
                  </div>
                </div>

                <div className="p-3 rounded-lg bg-amber-600/[0.05] border border-amber-500/20 text-sm text-slate-400">
                  <p className="font-medium text-amber-400 mb-1">Zoho Mail Setup:</p>
                  <ol className="list-decimal ml-4 space-y-1">
                    <li>Go to Zoho Mail → Settings → Security → App Passwords</li>
                    <li>Generate an App Password for "SMTP"</li>
                    <li>Use <code className="text-white bg-white/5 px-1 rounded">smtp.zoho.in</code> (India) or <code className="text-white bg-white/5 px-1 rounded">smtp.zoho.com</code> (International)</li>
                    <li>Port: 587 (TLS)</li>
                  </ol>
                </div>

                <div className="flex gap-3">
                  <Button onClick={handleSaveSmtp} disabled={smtpSaving} data-testid="save-smtp"
                    className="bg-amber-600 hover:bg-amber-700 text-white">
                    {smtpSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Save SMTP Settings
                  </Button>
                  <Button onClick={handleTestSmtp} disabled={smtpTesting || smtpForm.SMTP_ENABLED !== 'true'}
                    data-testid="test-smtp" variant="outline"
                    className="border-amber-600/30 text-amber-400 hover:bg-amber-600/10">
                    {smtpTesting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                    Send Test Email
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className="glass-card rounded-xl p-6 mt-6">
            <h4 className="text-sm font-semibold text-white mb-3">Email Templates (Auto-triggered)</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { name: 'Welcome Email', trigger: 'On plan purchase / admin user creation', color: 'text-emerald-400' },
                { name: 'Low Wallet Alert', trigger: 'When wallet balance < ₹500 (Enterprise/Premium)', color: 'text-amber-400' },
                { name: 'Low Minutes Alert', trigger: 'When remaining minutes < 20% of limit', color: 'text-amber-400' },
                { name: 'Plan Expiry Reminder', trigger: 'When plan is about to expire', color: 'text-red-400' },
                { name: 'Plan Change Confirmation', trigger: 'On upgrade or downgrade', color: 'text-cyan-400' },
              ].map((t, i) => (
                <div key={i} className="p-3 rounded-lg bg-white/[0.02] border border-white/5">
                  <div className={`text-sm font-medium ${t.color}`}>{t.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{t.trigger}</div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* ===== SEO TAB ===== */}
        <TabsContent value="seo">
          <div className="space-y-6">
            {/* Meta Tags */}
            <div className="glass-card rounded-xl p-6" data-testid="seo-meta-section">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-emerald-600/20 flex items-center justify-center">
                  <Search className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold font-['Outfit'] text-white">SEO Meta Tags</h3>
                  <p className="text-sm text-slate-400">Configure meta tags for better Google ranking</p>
                </div>
              </div>

              {seoLoading ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 text-emerald-400 animate-spin" /></div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <Label className="text-slate-300 text-sm mb-2 block">Site Title (60 chars max for Google)</Label>
                    <Input value={seoForm.site_title || ''} onChange={(e) => setSeoForm(p => ({ ...p, site_title: e.target.value }))}
                      placeholder="DotCall — AI Voice Agent Platform" data-testid="seo-title"
                      className="bg-slate-950 border-white/10 text-white" maxLength={70} />
                    <p className="text-xs text-slate-600 mt-1">{(seoForm.site_title || '').length}/60 characters</p>
                  </div>
                  <div>
                    <Label className="text-slate-300 text-sm mb-2 block">Meta Description (160 chars max)</Label>
                    <textarea value={seoForm.meta_description || ''} onChange={(e) => setSeoForm(p => ({ ...p, meta_description: e.target.value }))}
                      placeholder="Automate sales and support calls with AI voice agents..."
                      data-testid="seo-description" maxLength={170} rows={3}
                      className="w-full bg-slate-950 border border-white/10 text-white rounded-md px-3 py-2 text-sm resize-none focus:ring-1 focus:ring-emerald-500" />
                    <p className="text-xs text-slate-600 mt-1">{(seoForm.meta_description || '').length}/160 characters</p>
                  </div>
                  <div>
                    <Label className="text-slate-300 text-sm mb-2 block">Meta Keywords (comma-separated)</Label>
                    <Input value={seoForm.meta_keywords || ''} onChange={(e) => setSeoForm(p => ({ ...p, meta_keywords: e.target.value }))}
                      placeholder="AI voice agent, voice AI India, automated calling..."
                      data-testid="seo-keywords"
                      className="bg-slate-950 border-white/10 text-white" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300 text-sm mb-2 block">Canonical URL</Label>
                      <Input value={seoForm.canonical_url || ''} onChange={(e) => setSeoForm(p => ({ ...p, canonical_url: e.target.value }))}
                        placeholder="https://dotcall.ai" data-testid="seo-canonical"
                        className="bg-slate-950 border-white/10 text-white" />
                    </div>
                    <div>
                      <Label className="text-slate-300 text-sm mb-2 block">Robots Directive</Label>
                      <Input value={seoForm.robots || ''} onChange={(e) => setSeoForm(p => ({ ...p, robots: e.target.value }))}
                        placeholder="index, follow" data-testid="seo-robots"
                        className="bg-slate-950 border-white/10 text-white" />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Open Graph */}
            <div className="glass-card rounded-xl p-6" data-testid="seo-og-section">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-blue-600/20 flex items-center justify-center">
                  <Globe className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold font-['Outfit'] text-white">Open Graph & Social</h3>
                  <p className="text-sm text-slate-400">How your site appears when shared on social media</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300 text-sm mb-2 block">OG Title</Label>
                    <Input value={seoForm.og_title || ''} onChange={(e) => setSeoForm(p => ({ ...p, og_title: e.target.value }))}
                      placeholder="DotCall — AI Voice Agents" data-testid="seo-og-title"
                      className="bg-slate-950 border-white/10 text-white" />
                  </div>
                  <div>
                    <Label className="text-slate-300 text-sm mb-2 block">OG Type</Label>
                    <Input value={seoForm.og_type || ''} onChange={(e) => setSeoForm(p => ({ ...p, og_type: e.target.value }))}
                      placeholder="website" className="bg-slate-950 border-white/10 text-white" />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300 text-sm mb-2 block">OG Description</Label>
                  <textarea value={seoForm.og_description || ''} onChange={(e) => setSeoForm(p => ({ ...p, og_description: e.target.value }))}
                    placeholder="Automate your business calls with AI..." rows={2}
                    data-testid="seo-og-description"
                    className="w-full bg-slate-950 border border-white/10 text-white rounded-md px-3 py-2 text-sm resize-none" />
                </div>
                <div>
                  <Label className="text-slate-300 text-sm mb-2 block">OG Image URL</Label>
                  <Input value={seoForm.og_image || ''} onChange={(e) => setSeoForm(p => ({ ...p, og_image: e.target.value }))}
                    placeholder="https://..." data-testid="seo-og-image"
                    className="bg-slate-950 border-white/10 text-white" />
                  {seoForm.og_image && (
                    <div className="mt-2 p-2 rounded border border-white/5 bg-white/[0.02] inline-block">
                      <img src={seoForm.og_image} alt="OG Preview" className="h-16 w-auto rounded" onError={(e) => { e.target.style.display = 'none'; }} />
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-slate-300 text-sm mb-2 block">Twitter Card Type</Label>
                  <Input value={seoForm.twitter_card || ''} onChange={(e) => setSeoForm(p => ({ ...p, twitter_card: e.target.value }))}
                    placeholder="summary_large_image"
                    className="bg-slate-950 border-white/10 text-white" />
                </div>
              </div>
            </div>

            {/* Structured Data */}
            <div className="glass-card rounded-xl p-6" data-testid="seo-structured-section">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-purple-600/20 flex items-center justify-center">
                  <Code className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold font-['Outfit'] text-white">Structured Data (JSON-LD)</h3>
                  <p className="text-sm text-slate-400">Schema.org markup for rich search results</p>
                </div>
              </div>
              <textarea value={structuredDataStr} onChange={(e) => setStructuredDataStr(e.target.value)}
                data-testid="seo-structured-data"
                rows={10}
                className="w-full bg-slate-950 border border-white/10 text-white rounded-md px-3 py-2 text-sm font-mono resize-y" />
              <p className="text-xs text-slate-500 mt-2">Must be valid JSON. This will be injected as JSON-LD in the page head.</p>
            </div>

            {/* Sitemap */}
            <div className="glass-card rounded-xl p-6" data-testid="seo-sitemap-section">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-cyan-600/20 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold font-['Outfit'] text-white">Sitemap & Robots.txt</h3>
                    <p className="text-sm text-slate-400">Auto-generated at /api/sitemap.xml and /api/robots.txt</p>
                  </div>
                </div>
                <Button onClick={addSitemapPage} size="sm" variant="outline"
                  className="border-cyan-600/30 text-cyan-400 hover:bg-cyan-600/10">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add Page
                </Button>
              </div>

              <div className="space-y-2">
                {(seoForm.sitemap_pages || []).map((page, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-white/[0.02] border border-white/5">
                    <Input value={page.url} onChange={(e) => updateSitemapPage(i, 'url', e.target.value)}
                      placeholder="/page-url" className="bg-slate-950 border-white/10 text-white flex-1 text-sm h-8" />
                    <Input value={page.priority} onChange={(e) => updateSitemapPage(i, 'priority', e.target.value)}
                      placeholder="0.5" className="bg-slate-950 border-white/10 text-white w-20 text-sm h-8" />
                    <Input value={page.changefreq} onChange={(e) => updateSitemapPage(i, 'changefreq', e.target.value)}
                      placeholder="monthly" className="bg-slate-950 border-white/10 text-white w-28 text-sm h-8" />
                    <Button size="sm" variant="ghost" onClick={() => removeSitemapPage(i)}
                      className="text-slate-500 hover:text-red-400 h-8 w-8 p-0">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
                {(seoForm.sitemap_pages || []).length === 0 && (
                  <p className="text-sm text-slate-600 text-center py-4">No sitemap pages configured. Add pages to generate sitemap.xml.</p>
                )}
              </div>
            </div>

            {/* Custom Head Tags */}
            <div className="glass-card rounded-xl p-6" data-testid="seo-custom-section">
              <div className="flex items-center gap-3 mb-4">
                <Code className="w-5 h-5 text-slate-400" />
                <div>
                  <h3 className="text-base font-semibold font-['Outfit'] text-white">Custom Head Tags</h3>
                  <p className="text-xs text-slate-400">Additional HTML to inject in the head (e.g., Google Analytics, verification tags)</p>
                </div>
              </div>
              <textarea value={seoForm.custom_head_tags || ''} onChange={(e) => setSeoForm(p => ({ ...p, custom_head_tags: e.target.value }))}
                data-testid="seo-custom-tags"
                rows={5} placeholder='<meta name="google-site-verification" content="..." />'
                className="w-full bg-slate-950 border border-white/10 text-white rounded-md px-3 py-2 text-sm font-mono resize-y" />
            </div>

            {/* Save SEO Button */}
            <Button onClick={handleSaveSeo} disabled={seoSaving} data-testid="save-seo"
              className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {seoSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save All SEO Settings
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
